// ===============================================================
// 💰 BuyCoinsPage — صفحة شراء التوكينز (Coins للشباب)
// ✅ SAFE Fake Topup
// ✅ FINAL (c-based IDs)
// path: src/components/wallet/BuyCoinsPage.js
// ===============================================================
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth, db } from "../../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import coinsGold from "../../assets/gold.png";
import {
  fetchRatesEUR,
  convertFromEUR,
  formatPrice,
} from "../../utils/currency";

import TopBar from "../common/TopBar";
import LivoraToast from "../common/LivoraToast";
import useLivoraToast from "../../hooks/useLivoraToast";
import "../common/LivoraFeedback.css";
import "./BuyCoinsPage.css";
import { ENABLE_COIN_PURCHASES } from "../../config/featureFlags";

// ✅ باقات الكوينز (c system)
const COIN_PACKAGES = [
  { id: "c100", coins: 100, price: 1.49, discount: 0 },
  { id: "c250", coins: 250, price: 2.99, discount: 5 },
  { id: "c500", coins: 500, price: 5.49, discount: 10 },
  { id: "c1000", coins: 1000, price: 9.99, discount: 15 },
  { id: "c2500", coins: 2500, price: 22.99, discount: 20 },
  { id: "c5000", coins: 5000, price: 42.99, discount: 25 },
  { id: "c7500", coins: 7500, price: 59.99, discount: 30 },
  { id: "c10000", coins: 10000, price: 74.99, discount: 35 },
];
// ===============================================================
// UUID helper
// ===============================================================
function makeClientTxId() {
  try {
    if (window?.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {}

  return `tx_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export default function BuyCoinsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [balance, setBalance] = useState(0);
  const [currency, setCurrency] = useState("EUR");
  const [rates, setRates] = useState({});
  const [buyingId, setBuyingId] = useState(null);
  const [fxReady, setFxReady] = useState(false);
  const [paymentModalPkg, setPaymentModalPkg] = useState(null);

  const { toast, showToast } = useLivoraToast();
  const functions = useMemo(() => getFunctions(), []);

  const closePaymentModal = useCallback(() => {
    if (buyingId) return;
    setPaymentModalPkg(null);
  }, [buyingId]);

  // ===============================================================
  // 🔥 Live Balance
  // ===============================================================
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const ref = doc(db, "users", user.uid);

    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;

      const data = snap.data() || {};
      setBalance(Number(data.coins ?? 0));
    });

    return () => unsub();
  }, []);

  // ===============================================================
  // 🌍 Currency
  // ===============================================================
  useEffect(() => {
    // Phase PLAY-COMPLIANCE-COIN-PURCHASES-DISABLE: no package prices are
    // ever shown while disabled, so there is no reason to fetch currency
    // conversion rates for a purchase flow that cannot be reached.
    if (!ENABLE_COIN_PURCHASES) {
      setFxReady(true);
      return;
    }

    const run = async () => {
      try {
        setCurrency("EUR");
        const r = await fetchRatesEUR();
        setRates(r || {});
      } catch (e) {
        console.log("fx failed", e);
        setCurrency("EUR");
        setRates({});
      } finally {
        setFxReady(true);
      }
    };

    run();
  }, []);

  // ===============================================================
  // 📦 Open payment card (in-app; no browser dialogs)
  // ===============================================================
  const openPaymentModal = (pkg) => {
    // Phase PLAY-COMPLIANCE-COIN-PURCHASES-DISABLE: must never open the
    // payment card while purchases are disabled - defense in depth, the
    // disabled render branch below never mounts the package grid that
    // would call this anyway.
    if (!ENABLE_COIN_PURCHASES) return;
    if (!pkg?.id) return;
    if (buyingId) return;

    const user = auth.currentUser;
    if (!user) {
      showToast(
        "error",
        t("coins.loginRequired", { defaultValue: "You need to log in first" })
      );
      navigate("/");
      return;
    }

    setPaymentModalPkg(pkg);
  };

  // ===============================================================
  // 🧾 Confirm purchase — same fakeBuyCoins payload as before
  // ===============================================================
  const confirmPurchase = async () => {
    // Phase PLAY-COMPLIANCE-COIN-PURCHASES-DISABLE: must never call
    // fakeBuyCoins while purchases are disabled - defense in depth, the
    // disabled render branch below never mounts the payment modal that
    // would call this anyway.
    if (!ENABLE_COIN_PURCHASES) return;

    const pkg = paymentModalPkg;
    if (!pkg?.id) return;
    if (buyingId) return;

    const user = auth.currentUser;
    if (!user) {
      setPaymentModalPkg(null);
      showToast(
        "error",
        t("coins.loginRequired", { defaultValue: "You need to log in first" })
      );
      navigate("/");
      return;
    }

    try {
      setBuyingId(pkg.id);

      const fakeBuyCoins = httpsCallable(functions, "fakeBuyCoins");

      console.log("SEND PACKAGE:", pkg.id); // ✅ debug

      await fakeBuyCoins({
        packageId: pkg.id, // c100 ✔️
        clientTxId: makeClientTxId(),
      });

      setPaymentModalPkg(null);
      showToast(
        "success",
        t("coins.purchaseSuccess", {
          defaultValue: "Purchase completed and balance added to your account",
        })
      );
    } catch (e) {
      console.error("fakeBuyCoins error:", e);
      setPaymentModalPkg(null);
      showToast(
        "error",
        e?.message ||
          t("coins.purchaseError", {
            defaultValue: "An error occurred during purchase",
          })
      );
    } finally {
      setBuyingId(null);
    }
  };

  // Phase PLAY-COMPLIANCE-COIN-PURCHASES-DISABLE: while coin purchases
  // are disabled, render the existing coin balance (still fully usable)
  // plus a neutral unavailable notice only - no package prices, payment
  // methods, purchase-confirmation buttons, or payment modal, and no
  // redirect to any external payment method. The original purchase UI
  // below is preserved unchanged (not deleted) for reactivation later.
  if (!ENABLE_COIN_PURCHASES) {
    return (
      <div className="buycoins-container">
        <TopBar
          title={t("coins.buyTitle", { defaultValue: "Coins" })}
          onBack={() => navigate(-1)}
        />

        <div className="wallet-balance-box">
          <img
            src={coinsGold}
            alt={t("coins.alt", { defaultValue: "Coins" })}
            className="wallet-balance-icon-img"
          />
          <span className="wallet-balance-value">{balance}</span>
        </div>

        <div
          style={{
            margin: "16px",
            padding: "14px 16px",
            borderRadius: 10,
            border: "1px solid #37538f",
            background: "#0f1a2f",
            color: "#9fb2d8",
            fontSize: 14,
            textAlign: "center",
          }}
        >
          {t("coins.purchasesDisabledNotice", {
            defaultValue: "Coin purchases are currently unavailable.",
          })}
        </div>

        <div className="wallet-support" onClick={() => navigate("/support")}>
          {t("support.title", { defaultValue: "Customer support" })} 🧑‍💼
        </div>

        <LivoraToast toast={toast} />
      </div>
    );
  }

  // ===============================================================
  // UI
  // ===============================================================
  return (
    <div className="buycoins-container">
      <TopBar title={t("coins.buyTitle", { defaultValue: "Coins" })} onBack={() => navigate(-1)} />

      <div className="wallet-balance-box">
        <img
          src={coinsGold}
          alt={t("coins.alt", { defaultValue: "Coins" })}
          className="wallet-balance-icon-img"
        />
        <span className="wallet-balance-value">{balance}</span>
      </div>

      <div className="wallet-grid">
        {COIN_PACKAGES.map((p) => {
          const localPrice = fxReady
            ? convertFromEUR(p.price, rates, currency)
            : null;

          const isBuying = buyingId === p.id;

          return (
            <div
              key={p.id}
              className={`wallet-card ${isBuying ? "is-buying" : ""}`}
              onClick={() => openPaymentModal(p)}
            >
              <div className="wallet-card-top">
                <div className="wallet-coins">{p.coins}</div>

                {p.discount > 0 && (
                  <div className="wallet-discount">
                    {t("coins.discount", {
                      defaultValue: "{{percent}}% OFF",
                      percent: p.discount,
                    })}
                  </div>
                )}

                <img
                  src={coinsGold}
                  alt={t("coins.alt", { defaultValue: "Coins" })}
                  className="wallet-coins-img"
                />
              </div>

              <div className="wallet-price">
                {isBuying
                  ? t("coins.buying", { defaultValue: "..." })
                  : fxReady
                  ? formatPrice(localPrice, currency)
                  : formatPrice(p.price, "EUR")}
              </div>
            </div>
          );
        })}
      </div>

      <div className="wallet-support" onClick={() => navigate("/support")}>
        {t("support.title", { defaultValue: "Customer support" })} 🧑‍💼
      </div>

      {paymentModalPkg && (
        <div className="gate-overlay" onClick={closePaymentModal}>
          <div
            className="gatecard-center buycoins-payment-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="buycoins-payment-title"
          >
            <div className="gatecard-title" id="buycoins-payment-title">
              {t("coins.paymentTitle", { defaultValue: "Confirm purchase" })}
            </div>

            <div className="buycoins-payment-visual">
              <img
                src={coinsGold}
                alt=""
                className="buycoins-payment-coin"
                aria-hidden
              />
              <div className="buycoins-payment-coins-amount">
                {paymentModalPkg.coins}
              </div>
            </div>

            <div className="buycoins-payment-eur">
              {formatPrice(paymentModalPkg.price, "EUR")}
            </div>

            <div className="gatecard-sub buycoins-payment-sub">
              {t("coins.paymentConfirmShort", {
                defaultValue:
                  "Add {{coins}} coins to your wallet for the price below.",
                coins: paymentModalPkg.coins,
              })}
            </div>

            <div className="gatecard-actions">
              <button
                type="button"
                className="gate-btn gate-btn-cancel"
                onClick={closePaymentModal}
                disabled={!!buyingId}
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>

              <button
                type="button"
                className="gate-btn gate-btn-pay"
                onClick={confirmPurchase}
                disabled={!!buyingId}
              >
                {buyingId === paymentModalPkg.id
                  ? t("coins.buying", { defaultValue: "..." })
                  : t("coins.confirmPurchase", {
                      defaultValue: "Confirm purchase",
                    })}
              </button>
            </div>
          </div>
        </div>
      )}

      <LivoraToast toast={toast} />
    </div>
  );
}
